import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  adminListApps,
  adminCreateApp,
  adminUpdateApp,
  adminDeleteApp,
  adminListMessages,
  adminDeleteMessage,
} from "@/lib/adminApi";
import {
  Plus,
  Edit3,
  Trash2,
  LogOut,
  ExternalLink,
  Activity,
  Mail,
  Layers,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import AppForm from "@/components/admin/AppForm";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salió mal";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState("apps");
  const [apps, setApps] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | appObject

  const refreshAll = async () => {
    setLoading(true);
    try {
      const [appsData, msgsData] = await Promise.all([
        adminListApps(),
        adminListMessages(),
      ]);
      setApps(appsData);
      setMessages(msgsData);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
  }, []);

  const handleSave = async (form) => {
    try {
      if (editing === "new") {
        await adminCreateApp(form);
        toast.success("App creada");
      } else {
        const { slug, ...rest } = form;
        await adminUpdateApp(editing.slug, rest);
        toast.success("App actualizada");
      }
      setEditing(null);
      await refreshAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const handleDelete = async (app) => {
    if (
      !window.confirm(
        `¿Eliminar la app "${app.name}"?\nSe borra la app y su contador de clicks.`
      )
    )
      return;
    try {
      await adminDeleteApp(app.slug);
      toast.success("App eliminada");
      await refreshAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const handleDeleteMessage = async (m) => {
    if (!window.confirm(`¿Eliminar el mensaje de ${m.name}?`)) return;
    try {
      await adminDeleteMessage(m.id);
      toast.success("Mensaje eliminado");
      setMessages((prev) => prev.filter((x) => x.id !== m.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-grain">
      {/* Header */}
      <header className="sticky top-0 z-40 glass-strong border-b border-border">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              data-testid="back-to-site"
              onClick={() => navigate("/")}
              className="w-9 h-9 grid place-items-center border border-border hover:border-foreground transition-colors"
              aria-label="Volver al sitio"
            >
              <ArrowLeft size={14} />
            </button>
            <div>
              <div className="font-display font-bold text-lg leading-none">
                admin<span className="text-[hsl(var(--accent-green))]">_</span>
              </div>
              <div className="font-mono text-[10px] text-muted-foreground tracking-[0.15em] uppercase mt-0.5">
                {user?.email}
              </div>
            </div>
          </div>

          <button
            data-testid="logout-btn"
            onClick={() => {
              logout();
              navigate("/");
            }}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] px-3 py-2 border border-border hover:border-destructive hover:text-destructive transition-colors"
          >
            <LogOut size={13} />
            <span className="hidden sm:inline">salir</span>
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-8">
          <TabBtn active={tab === "apps"} onClick={() => setTab("apps")} testId="tab-apps">
            <Layers size={14} />
            Apps ({apps.length})
          </TabBtn>
          <TabBtn
            active={tab === "messages"}
            onClick={() => setTab("messages")}
            testId="tab-messages"
          >
            <Mail size={14} />
            Mensajes ({messages.length})
          </TabBtn>
        </div>

        {tab === "apps" && (
          <section data-testid="apps-tab">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight">
                Mis aplicaciones
              </h2>
              <button
                data-testid="new-app-btn"
                onClick={() => setEditing("new")}
                className="inline-flex items-center gap-2 bg-[hsl(var(--accent-green))] text-background font-mono font-bold uppercase tracking-[0.15em] text-xs px-4 py-2.5 border-2 border-[hsl(var(--accent-green))] hover:bg-transparent hover:text-[hsl(var(--accent-green))] transition-colors"
              >
                <Plus size={14} />
                <span className="hidden sm:inline">Nueva app</span>
              </button>
            </div>

            {loading && (
              <div className="font-mono text-sm text-muted-foreground">cargando...</div>
            )}

            {!loading && apps.length === 0 && (
              <div className="border border-border p-10 text-center">
                <p className="font-mono text-sm text-muted-foreground mb-4">
                  // todavía no agregaste ninguna app
                </p>
                <button
                  onClick={() => setEditing("new")}
                  className="font-mono text-xs uppercase tracking-[0.15em] px-4 py-2 border border-border hover:border-foreground transition-colors"
                >
                  + crear la primera
                </button>
              </div>
            )}

            <div className="space-y-3">
              {apps.map((app) => (
                <div
                  key={app.slug}
                  data-testid={`admin-app-${app.slug}`}
                  className="glass p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-1">
                      <h3 className="font-display font-bold text-lg truncate">
                        {app.name}
                      </h3>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground border border-border px-2 py-0.5">
                        {app.category}
                      </span>
                      {app.featured && (
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--accent-green))] border border-[hsl(var(--accent-green))/0.4] px-2 py-0.5">
                          ★ featured
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate mb-2">
                      {app.tagline}
                    </p>
                    <div className="flex items-center gap-4 font-mono text-[11px] text-muted-foreground">
                      <a
                        href={app.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 hover:text-[hsl(var(--accent-cyan))] truncate max-w-[260px]"
                      >
                        <ExternalLink size={11} />
                        {app.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                      </a>
                      <span className="inline-flex items-center gap-1">
                        <Activity size={11} className="text-[hsl(var(--accent-green))]" />
                        {(app.clicks || 0).toLocaleString("es-AR")} clicks
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      data-testid={`edit-${app.slug}`}
                      onClick={() => setEditing(app)}
                      className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] px-3 py-2 border border-border hover:border-[hsl(var(--accent-cyan))] hover:text-[hsl(var(--accent-cyan))] transition-colors"
                    >
                      <Edit3 size={13} />
                      editar
                    </button>
                    <button
                      data-testid={`delete-${app.slug}`}
                      onClick={() => handleDelete(app)}
                      className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] px-3 py-2 border border-border hover:border-destructive hover:text-destructive transition-colors"
                      aria-label={`Eliminar ${app.name}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {tab === "messages" && (
          <section data-testid="messages-tab">
            <h2 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight mb-6">
              Mensajes recibidos
            </h2>

            {loading && (
              <div className="font-mono text-sm text-muted-foreground">cargando...</div>
            )}

            {!loading && messages.length === 0 && (
              <div className="border border-border p-10 text-center">
                <p className="font-mono text-sm text-muted-foreground">
                  // sin mensajes todavía
                </p>
              </div>
            )}

            <div className="space-y-3">
              {messages.map((m) => (
                <div
                  key={m.id}
                  data-testid={`message-${m.id}`}
                  className="glass p-5"
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="min-w-0">
                      <div className="font-display font-bold text-base">
                        {m.name}
                      </div>
                      <a
                        href={`mailto:${m.email}`}
                        className="font-mono text-xs text-[hsl(var(--accent-cyan))] hover:underline"
                      >
                        {m.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-[10px] text-muted-foreground tracking-wider">
                        {formatDate(m.created_at)}
                      </span>
                      <button
                        data-testid={`delete-msg-${m.id}`}
                        onClick={() => handleDeleteMessage(m)}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                        aria-label="Eliminar mensaje"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                    {m.message}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Form modal */}
      {editing && (
        <AppForm
          mode={editing === "new" ? "create" : "edit"}
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

const TabBtn = ({ active, onClick, children, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={[
      "inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] px-4 py-2 border transition-colors",
      active
        ? "bg-foreground text-background border-foreground"
        : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/60",
    ].join(" ")}
  >
    {children}
  </button>
);

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};
